import {
  ArchiveIcon,
  DownloadIcon,
  HashIcon,
  InboxIcon,
  MessagesSquareIcon,
  SearchIcon,
  SendIcon,
  UsersIcon,
} from 'lucide-react'
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from 'react'
import { useSearchParams } from 'react-router'

import { errorMessage } from '@/api/client'
import { collapse, type HistoryEntry } from './collapse'
import { splitHighlights } from './highlight'
import { hiddenSenders, showAllSenders, toggleSender, visibleMessages } from './agent-visibility'
import {
  instantToLocalInput,
  localInputToInstant,
  PRESET_LABELS,
  PRESETS,
  resolveBounds,
  selectedOption,
  type Preset,
  type WindowParams,
} from './time-window'
import { channelFilterIsUnknown } from './channel-filter'
import {
  buckets,
  extent,
  selectionFromBucket,
  selectionFromDrag,
  selectionPixels,
  toPixel,
} from './timeline'
import { DIRECT_ONLY, jumpLeavesTheFilteredView } from './search-jump'
import { sendOutcome } from './send-outcome'
import type { ConversationSummary, SearchGroup, SearchResults } from '@/api/types'
import { downloadMessagesCsv } from '@/api/endpoints'
import {
  isMissingPresence,
  MESSAGES_POLL_MS,
  useChannels,
  useConnections,
  useInbox,
  useMessages,
  useMessageIndex,
  usePresence,
  useSendMessage,
  useConversations,
  useSearch,
} from '@/api/queries'
import type {
  DeliveryResult,
  MessageState,
  SendMessageRequest,
  ZoneSummary,
} from '@/api/types'
import { PageBody, PageHeader } from '@/components/AppShell'
import { MessageStateTag } from '@/components/badges'
import { MissingPresenceNotice, PresencePanel } from '@/components/PresenceControl'
import { PausedZoneNotice, RequireZone } from '@/components/RequireZone'
import { EmptyState, ErrorState } from '@/components/states'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { browserTimeZone, formatAbsolute, formatClock, formatRelative, pluralise } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useReadOnly } from '@/staff/hooks'

/* -------------------------------------------------------------------------- */
/* History                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * A channel broadcast writes one `messages` row per member (§15), so the raw
 * history repeats the same text once per recipient. Collapse those back into
 * the single thing that was actually said, keeping the recipient names.
 */
/**
 * One tag for a fan-out whose recipients may be in different states.
 *
 * Reported worst-first, because the tag exists to answer "is anyone still owed
 * this?" and the answer must not be softened by the recipients for whom it is
 * settled. §19's `HELD` outranks everything: a held row is neither delivered
 * nor lost, and it stays that way until a human chooses DELIVER or DISCARD —
 * so it is the one state that names an outstanding decision.
 *
 * Collapsing `state !== 'QUEUED'` into "read" would report a held message as
 * having been read by a recipient that was never allowed to see it.
 */
function fanoutState(states: readonly MessageState[]): MessageState {
  if (states.includes('HELD')) return 'HELD'
  if (states.includes('QUEUED')) return 'QUEUED'
  if (states.includes('CONSUMED')) return 'CONSUMED'
  // WI5: below CONSUMED on purpose — if one member read it before another's copy expired, "read"
  // is the truer summary of the thing that was said.
  if (states.includes('EXPIRED')) return 'EXPIRED'
  return 'DISCARDED'
}

function HistoryRow({
  entry,
  myName,
  active,
  register,
}: {
  entry: HistoryEntry
  myName: string | undefined
  active?: boolean
  register?: (node: HTMLLIElement | null) => void
}) {
  const mine = myName !== undefined && entry.sender === myName

  return (
    <li
      ref={register}
      data-seq={entry.seq}
      data-sender={entry.sender}
      className={cn(
        'scroll-mt-2 rounded-lg border px-4 py-3',
        mine
          ? 'border-[color-mix(in_oklch,var(--primary)_30%,var(--border))] bg-[color-mix(in_oklch,var(--primary)_6%,var(--card))]'
          : 'border-border bg-card',
        active === true && 'ring-1 ring-primary/50',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm font-medium text-foreground">{entry.sender}</span>
        <span className="text-xs text-muted-foreground">→</span>
        {entry.source_type === 'channel' ? (
          <Badge variant="outline" className="font-mono">
            <HashIcon className="size-3" />
            {entry.source_name}
          </Badge>
        ) : (
          <span className="font-mono text-xs text-muted-foreground">
            {entry.recipients.join(', ')}
          </span>
        )}
        {entry.source_type === 'channel' && entry.recipients.length > 0 ? (
          <span className="text-xs text-muted-foreground">
            {pluralise(entry.recipients.length, 'recipient')}
          </span>
        ) : null}
        <span className="ml-auto flex items-center gap-2">
          {/* WI4: when a recipient first saw it, on hover. The state stays "queued" after a peek,
              so without this a message an agent has already looked at reads as untouched. */}
          <span
            title={
              entry.first_read_at === null
                ? undefined
                : `First read ${formatAbsolute(entry.first_read_at)}`
            }
          >
            <MessageStateTag state={fanoutState(entry.states)} />
          </span>
          <span
            className="font-mono text-xs text-muted-foreground tabular"
            title={formatAbsolute(entry.sent_at)}
          >
            {formatRelative(entry.sent_at)}
          </span>
        </span>
      </div>
      {/* text-xs, not text-sm: agent messages run long, and at the previous size two or three of
          them filled the card. leading-relaxed is kept so wrapped lines stay readable. */}
      <p className="mt-2 text-xs leading-relaxed text-pretty whitespace-pre-wrap text-foreground">
        {entry.message}
      </p>
    </li>
  )
}

/* -------------------------------------------------------------------------- */
/* Jump rail                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The rail: one row per CONVERSATION, and no per-message dots (decision D26).
 *
 * The user travels fast between conversations, so the rail is a conversation index rather than a
 * message index — simpler than what D19 shipped and strictly better at scale. D19's rail was
 * **capped** because it was one row per message across the whole zone and had to admit it did not
 * reach the beginning; one row per conversation is orders of magnitude smaller, so the cap is
 * removed rather than raised. A zone with a million messages across four hundred conversations
 * shows four hundred rows.
 *
 * `message_count` comes straight from the row — it is maintained on write precisely so that
 * rendering this list never counts messages.
 */
function ConversationRail({
  conversations,
  hasMore,
  activeId,
  onJump,
}: {
  conversations: ConversationSummary[]
  hasMore: boolean
  activeId: string | undefined
  onJump: (conversation: ConversationSummary) => void
}) {
  const activeRef = useRef<HTMLButtonElement | null>(null)
  const listRef = useRef<HTMLUListElement | null>(null)

  // Keep the active entry inside the rail's OWN scroll box, by arithmetic on the list — never
  // `scrollIntoView`. That call walks every scrollable ancestor, the document included, and with
  // `scroll-behavior: smooth` on `html` it started a window scroll on every load whose active
  // entry sat below the fold of the rail: the page crept 60-odd pixels on its own (bug-139, the
  // same class as bug-136). The rail moves; the page does not.
  useEffect(() => {
    const list = listRef.current
    const active = activeRef.current
    if (!list || !active) return
    const top = active.offsetTop - list.offsetTop
    const bottom = top + active.offsetHeight
    if (top < list.scrollTop) {
      list.scrollTop = top
    } else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight
    }
  }, [activeId])

  if (conversations.length === 0) return null

  return (
    <nav
      aria-label="Jump to a conversation"
      className="hidden w-52 shrink-0 flex-col border-l border-border pl-2 sm:flex"
    >
      <p className="pb-1 text-[0.625rem] font-medium tracking-wide text-muted-foreground uppercase">
        {pluralise(conversations.length, 'conversation')}
      </p>
      <ul ref={listRef} className="relative max-h-[36rem] space-y-px overflow-y-auto pr-1">
        {conversations.map((conversation) => {
          const active = conversation.id === activeId
          // An OPEN conversation renders as ongoing. Showing an end time of "now" would claim it
          // had finished, which is the opposite of what a null ended_at means.
          const ongoing = conversation.ended_at === null
          const channel = conversation.kind === 'CHANNEL'
          const who = channel
            ? `#${conversation.label ?? 'channel'}`
            : conversation.participants.join(', ')

          return (
            <li key={conversation.id}>
              <button
                type="button"
                ref={active ? activeRef : null}
                onClick={() => {
                  onJump(conversation)
                }}
                aria-current={active ? 'true' : undefined}
                title={`${who} · ${formatAbsolute(conversation.started_at)}${
                  ongoing ? ' · ongoing' : ` – ${formatAbsolute(conversation.ended_at as string)}`
                } · ${pluralise(conversation.message_count, 'message')}`}
                className={cn(
                  'flex w-full flex-col gap-0.5 rounded px-1.5 py-1 text-left text-[0.6875rem] transition-colors',
                  active
                    ? 'bg-[color-mix(in_oklch,var(--primary)_14%,transparent)] text-foreground'
                    : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                )}
              >
                <span className="flex items-center gap-1.5">
                  {/* A detected channel segment and a direct thread are different kinds of
                      record and must be tellable apart at a glance. */}
                  {channel ? (
                    <HashIcon className="size-2.5 shrink-0 opacity-70" />
                  ) : (
                    <UsersIcon className="size-2.5 shrink-0 opacity-70" />
                  )}
                  <span className="truncate font-mono">{who || 'conversation'}</span>
                </span>
                <span className="flex items-center gap-1 pl-4 text-[0.625rem] opacity-75">
                  <span>{formatClock(conversation.started_at)}</span>
                  <span aria-hidden>–</span>
                  <span className={ongoing ? 'text-primary' : undefined}>
                    {ongoing ? 'ongoing' : formatClock(conversation.ended_at as string)}
                  </span>
                  <span aria-hidden>·</span>
                  <span>{conversation.message_count}</span>
                  {conversation.archived ? <ArchiveIcon className="size-2.5 opacity-70" /> : null}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
      {hasMore ? (
        <p className="pt-1 text-[0.625rem] leading-snug text-muted-foreground">
          Older conversations exist — scroll the history to load them.
        </p>
      ) : null}
    </nav>
  )
}

/**
 * Search over message bodies. Text only — no regex (decision D26).
 *
 * Results feed the rail rather than replacing the history pane: search is navigation, and each hit
 * carries an anchor the history re-centres on. Grouped by conversation, because "17 matches across
 * 4 conversations" is a map and a flat list of 17 is not.
 */
function SearchBox({
  value,
  onChange,
  results,
  pending,
  onJump,
}: {
  value: string
  onChange: (next: string) => void
  results: SearchResults | undefined
  pending: boolean
  /**
   * The GROUP goes with the seq, not just the seq. Search is zone-wide while the history pane may
   * be narrowed to one channel, so the caller has to know which conversation the hit belongs to
   * before it can decide whether the jump lands (`pages/search-jump.ts`).
   */
  onJump: (group: SearchGroup, seq: number) => void
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={value}
          onChange={(event) => {
            onChange(event.target.value)
          }}
          placeholder='Search messages — try "exact phrase" or -exclude'
          aria-label="Search messages in this zone"
          className="h-8 w-full rounded-md border border-border bg-background pr-2 pl-8 text-xs outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      {value.trim().length === 0 ? null : pending ? (
        <p className="text-xs text-muted-foreground">Searching…</p>
      ) : results === undefined ? null : results.total === 0 ? (
        <p className="text-xs text-muted-foreground">No messages match that search.</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          <p className="text-xs text-muted-foreground">
            {pluralise(results.total, 'match')} across{' '}
            {pluralise(results.groups.length, 'conversation')}
            {results.truncated ? ' — narrow your search to see the rest' : ''}
          </p>
          <ul className="max-h-56 space-y-1.5 overflow-y-auto">
            {results.groups.map((group) => (
              <li key={group.conversation_id} className="rounded border border-border p-1.5">
                <p className="flex items-center gap-1.5 pb-1 text-[0.6875rem] text-muted-foreground">
                  {group.kind === 'CHANNEL' ? (
                    <HashIcon className="size-2.5" />
                  ) : (
                    <UsersIcon className="size-2.5" />
                  )}
                  <span className="truncate font-mono">
                    {group.kind === 'CHANNEL'
                      ? (group.label ?? 'channel')
                      : group.participants.join(', ')}
                  </span>
                  <span>· {pluralise(group.match_count, 'match')}</span>
                </p>
                <ul className="space-y-0.5">
                  {group.hits.slice(0, 4).map((hit) => (
                    <li key={hit.seq}>
                      <button
                        type="button"
                        onClick={() => {
                          onJump(group, hit.seq)
                        }}
                        className="w-full rounded px-1 py-0.5 text-left text-[0.6875rem] text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                      >
                        <span className="font-mono">{hit.sender}</span>{' '}
                        <Highlighted snippet={hit.snippet} />
                      </button>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/**
 * Renders `ts_headline`'s `<<match>>` markers as emphasis.
 *
 * The server deliberately does NOT send HTML — shipping markup from the database straight into a
 * browser is how an injection happens — so the markers are parsed here and turned into elements,
 * never into `dangerouslySetInnerHTML`.
 */
function Highlighted({ snippet }: { snippet: string }) {
  return (
    <>
      {splitHighlights(snippet).map((part, index) =>
        part.match ? (
          <mark
            key={index}
            className="rounded-[2px] bg-[color-mix(in_oklch,var(--primary)_25%,transparent)] px-0.5 text-foreground"
          >
            {part.text}
          </mark>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </>
  )
}

/* -------------------------------------------------------------------------- */
/* Filters                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Which slice of the zone's history to look at. Drives `channel` on the history query, the
 * conversation rail and the CSV export together, so all three narrow identically — an export
 * containing rows the page does not show would be a bug (see `downloadMessagesCsv`'s javadoc).
 *
 * The choice lives in the URL (`?channel=`) rather than component state, so a reload or a shared
 * link keeps the same view.
 */
function ChannelFilterSelect({
  zone,
  value,
  onChange,
}: {
  zone: ZoneSummary
  value: string | undefined
  onChange: (next: string | undefined) => void
}) {
  const channels = useChannels(zone.id)

  return (
    <div className="flex items-center gap-2">
      <Label htmlFor="messages-channel-filter">Channel</Label>
      <select
        id="messages-channel-filter"
        value={value ?? ''}
        onChange={(event) => {
          const next = event.target.value
          onChange(next === '' ? undefined : next)
        }}
        className="flex h-8 rounded-md border border-input bg-transparent px-2 py-1 text-xs shadow-xs"
      >
        <option value="">All messages</option>
        <option value={DIRECT_ONLY}>Direct only</option>
        {(channels.data ?? []).map((channel) => (
          <option key={channel.id} value={channel.id}>
            #{channel.name}
          </option>
        ))}
      </select>
    </div>
  )
}

/**
 * WHEN to look at, next to WHICH CHANNEL. One `<select>` of presets plus an optional custom range —
 * deliberately the smallest control that can express a range, rather than a two-handle scrubber:
 * "the last 24 hours" is the question people actually ask of a message log, and it is one click.
 *
 * The choice lives in the URL exactly as the channel filter does, in two shapes that mean different
 * things — `?when=` rolls with the clock, `?since=&until=` is fixed and shareable. See
 * `pages/time-window.ts` for why collapsing them into one would break shared links.
 *
 * Custom is a disclosure, not a mode: the two `datetime-local` inputs appear only once it is chosen,
 * so the toolbar stays one control wide the rest of the time.
 *
 * <p><strong>That disclosure is LOCAL state, not URL state</strong>, and it has to be. Deriving it
 * from the URL alone means choosing "Custom range…" on an unfiltered view opens nothing: there are
 * no bounds to write, so the URL does not change, so the fields never appear and the dropdown snaps
 * back to "All time". The filter belongs in the URL; the fact that someone has opened the fields to
 * type in does not.
 *
 * <p><strong>A range arriving from the TIMELINE must not open them.</strong> Two datetime inputs
 * wrap this toolbar onto another line, which measured as the whole page below shifting down 120px —
 * on every drag. Dragging is meant to be a fluid gesture and that made it lurch. So the fields open
 * only when someone asks for them: by choosing "Custom range…", by the small edit button beside the
 * dropdown, or by arriving on a link that already carries a range. A drag leaves the toolbar exactly
 * as it was, and the selection is legible on the chart itself.
 */
function TimeFilterSelect({
  params,
  customOpen,
  onCustomOpen,
  onPreset,
  onRange,
}: {
  params: WindowParams
  customOpen: boolean
  onCustomOpen: (open: boolean) => void
  onPreset: (next: Preset | undefined) => void
  onRange: (since: string | undefined, until: string | undefined) => void
}) {
  const fromUrl = selectedOption(params)
  const bounds = resolveBounds(params)
  const timeZone = browserTimeZone()
  const selected = customOpen && fromUrl === 'all' ? 'custom' : fromUrl

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Label htmlFor="messages-time-filter">When</Label>
      <select
        id="messages-time-filter"
        value={selected}
        onChange={(event) => {
          const next = event.target.value
          if (next === 'custom') {
            // Opening the fields must not itself narrow anything: seed them from whatever is
            // already showing, and leave the view alone until a bound is actually typed.
            onCustomOpen(true)
            onRange(bounds.since, bounds.until)
            return
          }
          onCustomOpen(false)
          onPreset(next === 'all' ? undefined : (next as Preset))
        }}
        className="flex h-8 rounded-md border border-input bg-transparent px-2 py-1 text-xs shadow-xs"
      >
        <option value="all">All time</option>
        {PRESETS.map((preset) => (
          <option key={preset} value={preset}>
            {PRESET_LABELS[preset]}
          </option>
        ))}
        <option value="custom">Custom range…</option>
      </select>

      {customOpen ? (
        <span className="flex flex-wrap items-center gap-1.5 text-xs">
          <input
            type="datetime-local"
            aria-label="From"
            value={instantToLocalInput(params.since)}
            onChange={(event) => {
              onRange(localInputToInstant(event.target.value), params.until)
            }}
            className="h-8 rounded-md border border-input bg-transparent px-2 py-1 text-xs shadow-xs"
          />
          <span aria-hidden className="text-muted-foreground">
            →
          </span>
          <input
            type="datetime-local"
            aria-label="To"
            value={instantToLocalInput(params.until)}
            onChange={(event) => {
              onRange(params.since, localInputToInstant(event.target.value))
            }}
            className="h-8 rounded-md border border-input bg-transparent px-2 py-1 text-xs shadow-xs"
          />
          {/* Which zone those two wall-clocks are in. `datetime-local` carries no zone and the API
              speaks UTC instants, so without this the fields are a guess -- and a shared
              `?since=&until=` link arrives showing DIFFERENT numbers to a reader in another zone
              while describing the same window. The compact offset is what is rendered; the IANA
              name, which is the part that disambiguates a DST boundary, is the tooltip, because it
              is long enough to wrap the toolbar. */}
          <span
            className="text-muted-foreground"
            title={timeZone.name === '' ? undefined : `Times shown in ${timeZone.name}`}
          >
            {timeZone.offset}
          </span>
        </span>
      ) : null}
    </div>
  )
}

/**
 * The timeline: every message in the zone as a histogram, with the current window highlighted and
 * draggable straight out of it.
 *
 * <p><strong>Its data is deliberately UNWINDOWED.</strong> `useMessageIndex` takes the same
 * `since`/`until` as everything else on this page, and passing them here would be the one mistake
 * that makes a scrubber useless: the chart would only ever show the period already selected, so
 * every drag could narrow and none could widen, and the reader would have no way back out except
 * the dropdown. The channel filter IS applied, because "the messages I am looking at" genuinely
 * excludes other channels — but time is what this control exists to change, so it must see all of
 * it. That is a second cache entry for the same endpoint, and it is the point of it.
 *
 * <p>Drag to select a range; click a bar for that bucket. The `When` dropdown and the custom
 * datetime fields remain the keyboard path — this is a pointer control, and pointer-only controls
 * must not be the only way to do anything (there is no keyboard affordance for a drag).
 *
 * <p>Capped server-side by `mcpseed.history.index-max`; `truncated` says the index does not reach
 * the beginning, and the chart says so too rather than implying the zone starts where the data does.
 */
function TimelineScrubber({
  zone,
  channel,
  bounds,
  onSelect,
  onEditRange,
}: {
  zone: ZoneSummary
  channel: string | undefined
  bounds: { since?: string; until?: string }
  onSelect: (since: string | undefined, until: string | undefined) => void
  onEditRange: () => void
}) {
  const index = useMessageIndex(zone.id, channel)
  // A CALLBACK ref, not `useRef`. The track does not exist on first render -- the component returns
  // a skeleton while the index loads -- so an effect that reads `ref.current` on mount finds null,
  // and with an empty dependency array it never runs again once the track appears. The width then
  // stays 0 forever, every drag maps to a zero-width window, and the scrubber silently does
  // nothing. State that changes when the element arrives is what makes the observer attach.
  const [track, setTrack] = useState<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(0)
  // Where a drag started, in track pixels. `undefined` means no drag is in progress.
  const [dragFrom, setDragFrom] = useState<number | undefined>(undefined)
  const [dragTo, setDragTo] = useState<number | undefined>(undefined)

  // The track is a flex child, so its width is only known after layout and changes with the window.
  useEffect(() => {
    if (track === null) return
    // Seeded synchronously as well as observed: ResizeObserver's first callback is asynchronous, and
    // a pointer-down arriving before it would be measured against a width of 0.
    setWidth(track.getBoundingClientRect().width)
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width
      if (measured !== undefined) setWidth(measured)
    })
    observer.observe(track)
    return () => {
      observer.disconnect()
    }
  }, [track])


  const items = index.data?.items
  const span = useMemo(() => extent(items ?? []), [items])
  // One bar per ~8px of track: fine enough to show shape, coarse enough that a single message is
  // still a clickable target.
  const bars = useMemo(
    () => (span === undefined ? [] : buckets(items ?? [], span, Math.max(8, Math.round(width / 8)))),
    [items, span, width],
  )
  const busiest = useMemo(() => bars.reduce((max, bar) => Math.max(max, bar.count), 0), [bars])

  /**
   * The drag lives on the WINDOW, and its listeners are attached IMPERATIVELY from the pointerdown.
   *
   * <p>Two separate failures forced this shape, and each one produced a scrubber that looked like it
   * had a bug in its arithmetic rather than in its plumbing:
   *
   * <ul>
   *   <li>`setPointerCapture` on the track is the tidier version and is not dependable at the
   *       edges: a drag finishing on the element's border — which is exactly what "select
   *       everything" looks like — releases outside its box and the `pointerup` never arrives.</li>
   *   <li>Attaching the listeners from an effect keyed on drag state loses fast drags outright. The
   *       effect runs on a later commit, and a pointer sequence delivered faster than React can
   *       render (a scripted drag, a trackpad flick) is over before anything is listening.</li>
   * </ul>
   *
   * <p>So the handlers go on immediately, in the event that starts the drag, and read everything
   * they need from a ref rather than from a closure over render-scoped values. React state is used
   * only to draw the selection rectangle, where being a frame late costs nothing.
   */
  const live = useRef({ span, width, bars, onSelect, xOf })
  live.current = { span, width, bars, onSelect, xOf }

  function beginDrag(event: { clientX: number }) {
    const startX = live.current.xOf(event)
    setDragFrom(startX)
    setDragTo(startX)

    const move = (moved: PointerEvent) => {
      setDragTo(live.current.xOf(moved))
    }
    const up = (released: PointerEvent) => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      const { span: s, width: w, bars: b, onSelect: select, xOf: at } = live.current
      const endX = at(released)
      setDragFrom(undefined)
      setDragTo(undefined)
      if (s === undefined) return

      const picked = selectionFromDrag(startX, endX, s, w)
      if (picked !== undefined) {
        select(picked.since, picked.until)
        return
      }
      // Too short to be a drag, so treat it as a click on whichever bar is under the pointer: a
      // stray click then selects something sensible rather than nothing, or an empty window the API
      // would reject.
      const bar = b.find((candidate) => {
        const left = toPixel(candidate.start, s, w)
        const right = toPixel(candidate.end, s, w)
        return endX >= left && endX <= right
      })
      if (bar !== undefined && bar.count > 0) {
        const bucket = selectionFromBucket(bar, s)
        select(bucket.since, bucket.until)
      }
    }

    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  if (index.isLoading) {
    return <Skeleton className="h-12 w-full" />
  }
  if (span === undefined) {
    // Nothing to scrub. Rendering an empty track would invite a drag that could only ever select a
    // window with nothing in it.
    return null
  }

  const selection = selectionPixels(bounds, span, width)
  const dragging =
    dragFrom !== undefined && dragTo !== undefined
      ? { left: Math.min(dragFrom, dragTo), right: Math.max(dragFrom, dragTo) }
      : undefined

  function xOf(event: { clientX: number }): number {
    const rect = track?.getBoundingClientRect()
    return rect === undefined ? 0 : event.clientX - rect.left
  }

  return (
    <div className="space-y-1">
      <div
        ref={setTrack}
        role="presentation"
        data-testid="timeline-track"
        className="relative h-12 w-full cursor-crosshair touch-none select-none rounded border border-border bg-muted/30"
        onPointerDown={beginDrag}
      >
        <div className="absolute inset-0 flex items-end gap-px px-px">
          {bars.map((bar) => (
            <div
              key={bar.start}
              // A bar with traffic is never invisible: a 1px floor is the difference between "no
              // messages here" and "a few", which is exactly what someone scans this strip for.
              style={{ height: bar.count === 0 ? 0 : `${Math.max(8, (bar.count / busiest) * 100)}%` }}
              className="flex-1 rounded-t-[1px] bg-primary/45"
            />
          ))}
        </div>

        {selection !== undefined && dragging === undefined ? (
          <div
            data-testid="timeline-selection"
            style={{ left: selection.left, width: Math.max(2, selection.right - selection.left) }}
            className="pointer-events-none absolute inset-y-0 border-x border-primary bg-primary/15"
          />
        ) : null}

        {dragging !== undefined ? (
          <div
            style={{ left: dragging.left, width: Math.max(1, dragging.right - dragging.left) }}
            className="pointer-events-none absolute inset-y-0 border-x border-primary bg-primary/25"
          />
        ) : null}
      </div>

      {/* One row, ALWAYS this height, whatever it says. What it says changes with the selection --
          the hint before one exists, the selected period afterwards -- and the "Edit" that opens the
          exact-datetime fields lives here rather than in the toolbar above. Putting it in the
          toolbar wrapped that row onto another line, which moved the whole page down 44px on every
          drag; here the text simply changes and nothing moves. */}
      <p className="flex items-center justify-between gap-2 text-[0.6875rem] text-muted-foreground">
        <span className="shrink-0">{formatClock(new Date(span.from).toISOString())}</span>
        <span className="truncate text-center">
          {selection === undefined ? (
            index.data?.truncated === true
              ? 'Drag to select · older messages are beyond this chart'
              : 'Drag to select a period, or click a bar'
          ) : (
            <>
              {formatAbsolute(bounds.since ?? new Date(span.from).toISOString())}
              {' → '}
              {bounds.until === undefined ? 'now' : formatAbsolute(bounds.until)}
              <button
                type="button"
                onClick={onEditRange}
                className="ml-2 underline underline-offset-2 hover:text-foreground"
              >
                Edit
              </button>
            </>
          )}
        </span>
        <span className="shrink-0">{formatClock(new Date(span.to).toISOString())}</span>
      </p>
    </div>
  )
}

/**
 * Which senders this browser is choosing to look at, per zone (`pages/agent-visibility.ts`).
 *
 * Client-side only: the history, rail and export stay complete underneath this — un-ticking a name
 * restores it instantly, with no refetch. `senders` is deliberately the UNFILTERED set seen in the
 * loaded window, so a hidden agent's own checkbox never disappears and cannot become un-hideable.
 */
function AgentsFilter({
  senders,
  hidden,
  onToggle,
  onShowAll,
}: {
  senders: string[]
  hidden: string[]
  onToggle: (name: string) => void
  onShowAll: () => void
}) {
  const [open, setOpen] = useState(false)
  const hiddenHere = hidden.filter((name) => senders.includes(name))

  return (
    <div className="relative">
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current)
        }}
      >
        <UsersIcon className="size-3.5" />
        Agents
        {hiddenHere.length > 0 ? (
          <Badge variant="secondary" className="font-mono tabular">
            {hiddenHere.length} hidden
          </Badge>
        ) : null}
      </Button>

      {open ? (
        <div className="absolute right-0 z-10 mt-1 w-56 rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-lg shadow-[oklch(0.17_0.012_256_/_0.12)]">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-[0.6875rem] font-medium tracking-wide text-muted-foreground uppercase">
              Show messages from
            </span>
            <button
              type="button"
              className="cursor-pointer text-xs text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
              disabled={hidden.length === 0}
              onClick={onShowAll}
            >
              Show all
            </button>
          </div>
          {senders.length === 0 ? (
            <p className="px-1.5 py-1 text-xs text-muted-foreground">Nobody has said anything yet.</p>
          ) : (
            <ul className="max-h-52 space-y-0.5 overflow-y-auto">
              {senders.map((name) => (
                <li key={name}>
                  <label className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-accent">
                    <input
                      type="checkbox"
                      className="size-3.5 accent-primary"
                      value={name}
                      checked={!hidden.includes(name)}
                      onChange={() => {
                        onToggle(name)
                      }}
                    />
                    <span className="truncate font-mono">{name}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Composer                                                                   */
/* -------------------------------------------------------------------------- */

type Mode = 'connections' | 'channel'

function Composer({ zone, myName }: { zone: ZoneSummary; myName: string | undefined }) {
  const connections = useConnections(zone.id)
  const channels = useChannels(zone.id)
  const send = useSendMessage()

  const [mode, setMode] = useState<Mode>('connections')
  const [selected, setSelected] = useState<string[]>([])
  const [channel, setChannel] = useState<string | undefined>(undefined)
  const [body, setBody] = useState('')
  const [undelivered, setUndelivered] = useState<DeliveryResult[]>([])
  // Reserved identities that are offline (WI3). The message IS sent — it waits in their queue —
  // so they are named for the sender's expectations, never treated as a failure.
  const [waitingOffline, setWaitingOffline] = useState<DeliveryResult[]>([])
  // A channel broadcast with no other member at all. Separate from `undelivered`: there are no
  // per-name outcomes to list, so there is nothing for that alert to render.
  const [nobodyInChannel, setNobodyInChannel] = useState(false)

  const readOnly = useReadOnly()
  const writable = zone.reachable && !readOnly

  // Offline reserved identities are addressable on purpose (WI3): the queue survives a disconnect,
  // so a message to one is `queued_offline` and read when it returns. The chip says "offline".
  const addressable = useMemo(
    () => (connections.data ?? []).filter((connection) => connection.name !== myName),
    [connections.data, myName],
  )

  const canSend =
    writable &&
    body.trim().length > 0 &&
    (mode === 'connections' ? selected.length > 0 : channel !== undefined)

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!canSend) return
    const payload: SendMessageRequest =
      mode === 'connections'
        ? { to: selected, message: body.trim() }
        : { channel: channel ?? '', message: body.trim() }
    send.mutate(
      { zoneId: zone.id, body: payload },
      {
        // A 200 is not proof of delivery, in two different ways — see `pages/send-outcome.ts`.
        // Clearing the box on a send that reached nobody would quietly lose the message.
        onSuccess: (result) => {
          const outcome = sendOutcome(mode, result)
          setUndelivered(outcome.undelivered)
          setWaitingOffline(outcome.waitingOffline)
          setNobodyInChannel(outcome.nobodyInChannel)
          if (outcome.clearBody) {
            setBody('')
          }
        },
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <SendIcon className="size-4 text-primary" />
          Say something
        </CardTitle>
        <CardDescription>
          Addressed either to named connections or to a channel — never both. A channel
          broadcast is queued once per member, offline ones included, and skips you.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4" hidden={readOnly}>
          <div className="inline-flex rounded-lg bg-muted p-1">
            <button
              type="button"
              onClick={() => {
                setMode('connections')
              }}
              className={cn(
                'flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-1 text-[0.8125rem] transition-colors',
                mode === 'connections'
                  ? 'bg-card text-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <UsersIcon className="size-3.5" />
              Connections
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('channel')
              }}
              className={cn(
                'flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-1 text-[0.8125rem] transition-colors',
                mode === 'channel'
                  ? 'bg-card text-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <HashIcon className="size-3.5" />
              Channel
            </button>
          </div>

          {mode === 'connections' ? (
            addressable.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nothing else is connected to this zone right now, and no agent name is reserved.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {addressable.map((connection) => {
                  const picked = selected.includes(connection.name)
                  return (
                    <button
                      key={connection.id}
                      type="button"
                      onClick={() => {
                        setSelected((current) =>
                          current.includes(connection.name)
                            ? current.filter((entry) => entry !== connection.name)
                            : [...current, connection.name],
                        )
                      }}
                      className={cn(
                        'cursor-pointer rounded-full border px-2.5 py-0.5 font-mono text-xs transition-colors',
                        picked
                          ? 'border-transparent bg-primary text-primary-foreground'
                          : 'border-border text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {connection.name}
                      <span className="ml-1.5 opacity-70">
                        {connection.state === 'CONNECTED' ? connection.type : 'offline'}
                      </span>
                    </button>
                  )
                })}
              </div>
            )
          ) : (channels.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">This zone has no channels yet.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {(channels.data ?? []).map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => {
                    setChannel(entry.name)
                  }}
                  className={cn(
                    'cursor-pointer rounded-full border px-2.5 py-0.5 font-mono text-xs transition-colors',
                    channel === entry.name
                      ? 'border-transparent bg-primary text-primary-foreground'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  #{entry.name}
                </button>
              ))}
            </div>
          )}

          <Textarea
            value={body}
            onChange={(event) => {
              setBody(event.target.value)
            }}
            placeholder="What do you want to tell them?"
            disabled={!writable || send.isPending}
            rows={3}
          />

          {isMissingPresence(send.error) ? (
            <MissingPresenceNotice zone={zone} action="Sending a message" />
          ) : send.isError ? (
            <Alert variant="destructive">
              <AlertTitle>Could not send that</AlertTitle>
              <AlertDescription>{errorMessage(send.error)}</AlertDescription>
            </Alert>
          ) : nobodyInChannel ? (
            <Alert variant="warning">
              <AlertTitle>Nobody received that</AlertTitle>
              <AlertDescription>
                <p>
                  {channel === undefined ? 'That channel' : `#${channel}`} has no other member, and
                  a broadcast skips its own sender — so there was nobody to write it for and{' '}
                  <strong>the message was not recorded</strong>. It is not in this zone&apos;s
                  history, its export or the conversation rail. An offline member would have
                  received it; there is none. Your text is still here: send it again once somebody
                  else has joined.
                </p>
              </AlertDescription>
            </Alert>
          ) : undelivered.length > 0 ? (
            <Alert variant="warning">
              <AlertTitle>
                {pluralise(undelivered.length, 'recipient')} did not receive that
              </AlertTitle>
              <AlertDescription>
                <ul className="space-y-1">
                  {undelivered.map((entry) => (
                    <li key={entry.name}>
                      <span className="font-mono">{entry.name}</span>
                      {entry.error === null ? null : ` — ${entry.error}`}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : null}

          {/* WI3: `queued_offline` is sent. The box was cleared; this only says who is not here
              to read it yet, so the sender does not sit waiting for an answer. */}
          {waitingOffline.length > 0 && !send.isError ? (
            <Alert variant="info">
              <AlertTitle>
                Sent — {pluralise(waitingOffline.length, 'recipient')}{' '}
                {waitingOffline.length === 1 ? 'is' : 'are'} offline
              </AlertTitle>
              <AlertDescription>
                <p>
                  The message is waiting for{' '}
                  <span className="font-mono">
                    {waitingOffline.map((entry) => entry.name).join(', ')}
                  </span>
                  . An offline identity keeps its name and its queue, and reads what is waiting the
                  moment it reconnects.
                </p>
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {mode === 'connections'
                ? `${pluralise(selected.length, 'recipient')} selected`
                : channel === undefined
                  ? 'Pick a channel'
                  : `Broadcasting to #${channel}`}
            </p>
            <Button type="submit" disabled={!canSend || send.isPending}>
              <SendIcon className="size-4" />
              {send.isPending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

/* -------------------------------------------------------------------------- */
/* Inbox                                                                      */
/* -------------------------------------------------------------------------- */

function Inbox({ zone, hasPresence }: { zone: ZoneSummary; hasPresence: boolean }) {
  const inbox = useInbox(zone.id, hasPresence)
  const rows = inbox.data ?? []

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <InboxIcon className="size-4 text-primary" />
          Your inbox
          {rows.length === 0 ? null : (
            <Badge variant="success" className="font-mono tabular">
              {rows.length}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          What is queued for your human connection, peeked non-destructively — reading here does
          not consume anything.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!hasPresence ? (
          <p className="text-sm text-muted-foreground">
            Join the zone to get a queue of your own.
          </p>
        ) : inbox.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing waiting for you.</p>
        ) : (
          <ul className="space-y-2.5">
            {rows.map((message, index) => (
              <li
                key={`${message.sent_at}-${message.sender}-${String(index)}`}
                className="rounded-lg border border-border bg-[var(--surface)] px-3.5 py-2.5"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-medium text-foreground">
                    {message.sender}
                  </span>
                  {message.source_type === 'channel' ? (
                    <Badge variant="outline" className="font-mono">
                      <HashIcon className="size-3" />
                      {message.source_name}
                    </Badge>
                  ) : null}
                  <span
                    className="ml-auto font-mono text-xs text-muted-foreground"
                    title={formatAbsolute(message.sent_at)}
                  >
                    {formatRelative(message.sent_at)}
                  </span>
                </div>
                <p className="mt-1.5 text-sm text-pretty whitespace-pre-wrap text-foreground">
                  {message.message}
                </p>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

function MessagesView({ zone }: { zone: ZoneSummary }) {
  const presence = usePresence(zone.id)
  const [searchParams, setSearchParams] = useSearchParams()
  // `undefined` = every message, `'none'` = direct only, otherwise a channel UUID. Lives in the URL
  // rather than component state so a reload keeps the same view.
  const urlChannelFilter = searchParams.get('channel') ?? undefined
  // The channel list. Hoisted above the reads because the filter is checked against it before it is
  // used: `filteredChannelName` below reads the same query, which is one cache entry, not two.
  const channels = useChannels(zone.id)
  // bug-128: a `?channel=` the loaded list cannot account for. Only ever true once the list has
  // actually ARRIVED -- see `channel-filter.ts`, where treating "still loading" as unknown is the
  // mistake that would break the channel jump on every page load.
  const channelFilterIsStale = channelFilterIsUnknown(
    urlChannelFilter,
    channels.data,
    channels.isSuccess,
  )
  const channelFilter = channelFilterIsStale ? undefined : urlChannelFilter
  // The time filter, in the same place and for the same reason: a reload or a shared link keeps the
  // view. `when` is a rolling preset, `since`/`until` a fixed range -- see `time-window.ts`.
  const windowParams: WindowParams = {
    when: searchParams.get('when') ?? undefined,
    since: searchParams.get('since') ?? undefined,
    until: searchParams.get('until') ?? undefined,
  }
  // Resolved once per render and passed to all four read paths, so the history, the rail and the
  // export cannot disagree about which window they are showing.
  const timeBounds = resolveBounds(windowParams)
  // The custom-range fields' disclosure. Owned here because two children need it: the dropdown opens
  // and closes it, and the timeline's caption offers the way back to it once a drag has set a range.
  // Seeded from the URL so a shared link to an explicit range arrives with its fields filled in.
  const [customOpen, setCustomOpen] = useState(() => selectedOption(windowParams) === 'custom')
  // `anchor` is set when the jump rail re-centres the window on an older message.
  const [anchor, setAnchor] = useState<number | undefined>(undefined)
  const messages = useMessages(zone.id, anchor, channelFilter, timeBounds)
  // The rail lists conversations, not messages (D26). Live conversations only -- archived ones
  // live on their own page, which is a hard split rather than a filter. Narrowed by the same
  // channel filter as the history, so the rail and the pane never disagree about what "this view"
  // means.
  const conversations = useConversations(zone.id, false, channelFilter, timeBounds)
  const [query, setQuery] = useState('')
  const search = useSearch(zone.id, query, false)
  // The channel the filter names, if it names one. Only needed to decide whether a search hit is
  // inside the current window: the results carry a channel NAME, the filter carries an id.
  const filteredChannelName = useMemo(
    () => (channels.data ?? []).find((entry) => entry.id === channelFilter)?.name,
    [channels.data, channelFilter],
  )
  const topSentinelRef = useRef<HTMLDivElement | null>(null)
  const scrollRef = useRef<HTMLUListElement | null>(null)
  const rowRefs = useRef(new Map<number, HTMLLIElement>())
  const [activeSeq, setActiveSeq] = useState<number | undefined>(undefined)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | undefined>(undefined)
  // Per-zone, client-side sender filter (`pages/agent-visibility.ts`) -- also read by the channel
  // conversation view on ChannelsPage, so it must not be re-derived differently here.
  const [hidden, setHidden] = useState<string[]>(() => hiddenSenders(zone.id))
  useEffect(() => {
    setHidden(hiddenSenders(zone.id))
  }, [zone.id])

  function toggleHidden(name: string) {
    setHidden(toggleSender(zone.id, name))
  }

  function showAll() {
    setHidden(showAllSenders(zone.id))
  }

  /**
   * Both writers clear the anchor and the scroll-spy position for the same reason
   * `changeChannelFilter` does: a different window is a different view, and the old anchor belongs
   * to whatever was on screen before it.
   */
  function changeWhen(next: Preset | undefined) {
    setSearchParams((current) => {
      const params = new URLSearchParams(current)
      // A preset and an explicit range are alternatives, so choosing one must clear the other --
      // otherwise a stale `since` sits in the URL, invisible behind a preset that outranks it, and
      // reappears the moment the preset is cleared.
      params.delete('since')
      params.delete('until')
      if (next === undefined) params.delete('when')
      else params.set('when', next)
      return params
    })
    setAnchor(undefined)
    setActiveSeq(undefined)
  }

  function changeRange(since: string | undefined, until: string | undefined) {
    setSearchParams((current) => {
      const params = new URLSearchParams(current)
      params.delete('when')
      if (since === undefined) params.delete('since')
      else params.set('since', since)
      if (until === undefined) params.delete('until')
      else params.set('until', until)
      // Nothing left to say: drop the marker rather than leave `?since=&until=` behind, so the
      // dropdown reads "All time" instead of an empty "Custom range".
      if (since === undefined && until === undefined) params.delete('when')
      return params
    })
    setAnchor(undefined)
    setActiveSeq(undefined)
  }

  function changeChannelFilter(next: string | undefined) {
    setSearchParams((current) => {
      const params = new URLSearchParams(current)
      if (next === undefined) params.delete('channel')
      else params.set('channel', next)
      return params
    })
    // A different channel is a different window: the old anchor and scroll-spy position belong to
    // whatever was on screen before, not to the newly narrowed conversation.
    setAnchor(undefined)
    setActiveSeq(undefined)
  }

  /**
   * bug-128: an unknown `?channel=` is dropped from the URL, not merely hidden from the control.
   *
   * <p>The URL is what the history, the rail and the CSV export are all fetched with, so leaving it
   * in place and only correcting the dropdown's displayed value would move the disagreement rather
   * than end it: the control would read "All messages" over a page still narrowed to a channel that
   * does not exist — every one of the three empty, and now with the control actively lying about
   * why. Rewriting makes one statement of the view, which is the invariant this page is held to.
   *
   * <p>`replace`, so the Back button still goes where the reader came from rather than back to the
   * broken link this just repaired.
   */
  useEffect(() => {
    if (!channelFilterIsStale) return
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current)
        params.delete('channel')
        return params
      },
      { replace: true },
    )
  }, [channelFilterIsStale, setSearchParams])

  // Pages arrive newest-page-first; flatten to ONE oldest-first list and collapse over the whole of
  // it, so a broadcast straddling a page boundary still renders as a single message.
  const rows = useMemo(
    () => [...(messages.data?.pages ?? [])].reverse().flatMap((page) => page.items),
    [messages.data],
  )
  const history = useMemo(() => collapse(rows), [rows])
  // The checkbox universe is the UNFILTERED set of senders in the loaded window -- if it were built
  // from `visibleHistory` a hidden sender's own checkbox would vanish along with their messages, and
  // there would be no way to bring them back.
  const senders = useMemo(
    () => Array.from(new Set(history.map((entry) => entry.sender))).sort((a, b) => a.localeCompare(b)),
    [history],
  )
  const visibleHistory = useMemo(() => visibleMessages(history, hidden), [history, hidden])
  const myName = presence.data?.name
  /**
   * Which conversation the reader is currently inside.
   *
   * Derived from the message the scroll-spy has landed on: the rail highlights the conversation
   * whose span contains it. Computed here rather than tracked separately, so the rail cannot drift
   * out of step with the history pane -- two sources of truth for "where am I" is how a highlight
   * ends up pointing somewhere the reader is not.
   */
  const activeConversationId = useMemo(() => {
    if (activeSeq === undefined) return undefined
    const rows = conversations.data?.conversations ?? []
    // Newest first, so the first whose anchor is at or below the active message is the one we are
    // in. A conversation with no anchor has nothing loaded to be inside.
    return rows.find(
      (conversation) => conversation.anchor_seq !== null && conversation.anchor_seq <= activeSeq,
    )?.id
  }, [activeSeq, conversations.data])

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = messages

  // Scroll anchoring. Prepending older messages moves everything down by their height, so without
  // restoring the offset the reader is thrown backwards every time a page loads — the classic bug
  // in reverse infinite scroll.
  const pendingRestore = useRef<number | undefined>(undefined)
  useLayoutEffect(() => {
    const el = scrollRef.current
    const previous = pendingRestore.current
    if (el === null || previous === undefined) return
    el.scrollTop = el.scrollHeight - previous
    pendingRestore.current = undefined
  }, [rows.length])

  // Pull older pages when the top of the list comes into view.
  useEffect(() => {
    const sentinel = topSentinelRef.current
    const el = scrollRef.current
    if (sentinel === null || el === null || !hasNextPage) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) {
          pendingRestore.current = el.scrollHeight
          void fetchNextPage()
        }
      },
      { root: el, rootMargin: '120px' },
    )
    observer.observe(sentinel)
    return () => {
      observer.disconnect()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  // Scroll-spy, so the rail tracks manual scrolling and not just clicks.
  useEffect(() => {
    const el = scrollRef.current
    if (el === null) return
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .map((e) => Number((e.target as HTMLElement).dataset.seq))
          .filter((seq) => !Number.isNaN(seq))
        if (visible.length > 0) setActiveSeq(Math.min(...visible))
      },
      { root: el, threshold: 0.5 },
    )
    for (const node of rowRefs.current.values()) observer.observe(node)
    return () => {
      observer.disconnect()
    }
  }, [visibleHistory.length])

  // Only stick to the bottom while the newest page is the window; after a jump, do not yank the
  // reader back to the end of the conversation.
  //
  // Scrolls the PANE by setting its own scrollTop, exactly as the scroll-anchoring effect above
  // does, rather than calling `scrollIntoView` on a marker inside it. `scrollIntoView` walks every
  // scrollable ancestor including the document, so each time the row count changed -- which is every
  // time the timeline selection changes -- it nudged the whole page as well as the pane. Measured at
  // 8px per change, small next to the 120px the toolbar was contributing, and still movement the
  // reader did not ask for.
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (el !== null && anchor === undefined) el.scrollTop = el.scrollHeight
  }, [visibleHistory.length, anchor])

  function jumpTo(seq: number) {
    // A non-finite seq must never reach the query string. `setAnchor(undefined - 1)` is NaN, which
    // serialises as `after=NaN`, which the API rejects as a malformed request -- and the console
    // then renders "Could not load the conversation", blaming the load rather than the click.
    // Cheap to guard, and it turns a broken page into a no-op.
    if (!Number.isFinite(seq)) return

    const node = rowRefs.current.get(seq)
    if (node !== undefined) {
      node.scrollIntoView({ block: 'center', behavior: 'smooth' })
      setActiveSeq(seq)
      return
    }
    // Not loaded: re-anchor the window there rather than paging back to it.
    setAnchor(seq - 1)
    setActiveSeq(seq)
  }

  /**
   * A jump from the SEARCH results, which are zone-wide even when the pane is narrowed to one
   * channel. Widening the view first is what makes the click land: re-anchoring inside a filter that
   * excludes the target moves the rail to an unrelated place and highlights a row that never
   * renders. `changeChannelFilter` resets the anchor, and `jumpTo` sets it again in the same batch,
   * so the order below is "widen, then anchor" and not two competing anchors.
   */
  function jumpToHit(group: SearchGroup, seq: number) {
    if (jumpLeavesTheFilteredView(channelFilter, filteredChannelName, group)) {
      changeChannelFilter(undefined)
    }
    jumpTo(seq)
  }

  async function exportCsv() {
    setExporting(true)
    setExportError(undefined)
    try {
      const { blob, filename } = await downloadMessagesCsv(zone.id, channelFilter, timeBounds)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename ?? `saltseed-${zone.name}-history.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (error) {
      setExportError(errorMessage(error))
    } finally {
      setExporting(false)
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Messages"
        title={`Conversation in ${zone.name}`}
        description="Everything ever said in this zone, in order. Messages are permanent: reading one moves it out of the queue, it is never removed."
        actions={
          <span className="text-xs text-muted-foreground">
            refreshing every {Math.round(MESSAGES_POLL_MS / 1000)}s
          </span>
        }
      />

      <PageBody>
        <PausedZoneNotice zone={zone} />
        <PresencePanel zone={zone} />

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
          <div className="space-y-6">
            <Card className="overflow-hidden">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessagesSquareIcon className="size-4 text-primary" />
                  Zone history
                  {/* Exports the WHOLE conversation, not the loaded window: the server streams it,
                      so this stays correct however far back the history goes. */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-auto"
                    disabled={exporting || history.length === 0}
                    onClick={() => {
                      void exportCsv()
                    }}
                  >
                    <DownloadIcon className="size-3.5" />
                    {exporting ? 'Exporting…' : 'Export CSV'}
                  </Button>
                </CardTitle>
                <CardDescription>
                  Every delivery in this zone, oldest first. A channel broadcast is stored once
                  per member; it is shown here as the single thing that was said.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-3 flex flex-wrap items-center gap-3">
                  <ChannelFilterSelect zone={zone} value={channelFilter} onChange={changeChannelFilter} />
                  <TimeFilterSelect
                    params={windowParams}
                    customOpen={customOpen}
                    onCustomOpen={setCustomOpen}
                    onPreset={changeWhen}
                    onRange={changeRange}
                  />
                  <AgentsFilter
                    senders={senders}
                    hidden={hidden}
                    onToggle={toggleHidden}
                    onShowAll={showAll}
                  />
                </div>
                {/* Under the controls rather than beside them: it is the width of the card that
                    makes a histogram readable, and the toolbar has to stay one line. It writes the
                    same `?since=&until=` the custom range does, so the dropdown follows a drag and
                    reads "Custom range" afterwards -- one filter, two ways in. */}
                <div className="mb-3">
                  <TimelineScrubber
                    zone={zone}
                    channel={channelFilter}
                    bounds={timeBounds}
                    onSelect={changeRange}
                    onEditRange={() => {
                      setCustomOpen(true)
                    }}
                  />
                </div>
                <div className="pb-3">
                  <SearchBox
                    value={query}
                    onChange={setQuery}
                    results={search.data}
                    pending={search.isFetching}
                    onJump={jumpToHit}
                  />
                </div>
                {messages.isError ? (
                  <ErrorState
                    error={messages.error}
                    title="Could not load the conversation"
                    onRetry={() => {
                      void messages.refetch()
                    }}
                  />
                ) : messages.isLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : history.length === 0 ? (
                  <EmptyState
                    icon={MessagesSquareIcon}
                    title="Nothing has been said in this zone"
                    description="Join the zone, pick a connection or a channel below, and send the first message. Agents connected over MCP will see it in their queue."
                  />
                ) : (
                  <>
                    <div className="flex gap-2">
                      <ul
                        ref={scrollRef}
                        aria-label="Zone history"
                        className="max-h-[36rem] flex-1 space-y-2.5 overflow-y-auto pr-1"
                      >
                        {/* Watched by an observer: reaching it pulls the next OLDER page. */}
                        <div ref={topSentinelRef} />
                        {hasNextPage ? (
                          <li className="py-1 text-center text-xs text-muted-foreground">
                            {isFetchingNextPage ? 'Loading older messages…' : 'Scroll up for more'}
                          </li>
                        ) : (
                          <li className="py-1 text-center text-xs text-muted-foreground">
                            The beginning of this conversation.
                          </li>
                        )}
                        {visibleHistory.length === 0 ? (
                          <li className="py-1 text-center text-xs text-muted-foreground">
                            Every sender in this window is hidden — see Agents above.
                          </li>
                        ) : null}
                        {visibleHistory.map((entry) => (
                          <HistoryRow
                            key={entry.key}
                            entry={entry}
                            myName={myName}
                            active={entry.seq === activeSeq}
                            register={(node) => {
                              if (node === null) rowRefs.current.delete(entry.seq)
                              else rowRefs.current.set(entry.seq, node)
                            }}
                          />
                        ))}
                      </ul>
                      <ConversationRail
                        conversations={conversations.data?.conversations ?? []}
                        hasMore={conversations.data?.has_more ?? false}
                        activeId={activeConversationId}
                        onJump={(conversation) => {
                          // Anchor on the conversation's first message, reusing D19's keyset
                          // cursor rather than inventing a second navigation mechanism.
                          if (conversation.anchor_seq !== null) jumpTo(conversation.anchor_seq)
                        }}
                      />
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <p className="text-xs text-muted-foreground">
                        {`${pluralise(visibleHistory.length, 'message')} loaded`}
                        {conversations.data === undefined
                          ? ''
                          : ` · ${pluralise(conversations.data.conversations.length, 'conversation')}`}
                        {anchor === undefined ? '' : ' · viewing earlier history'}
                      </p>
                      {anchor === undefined ? null : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setAnchor(undefined)
                          }}
                        >
                          Back to the latest
                        </Button>
                      )}
                      {exportError === undefined ? null : (
                        <span className="text-xs text-destructive">{exportError}</span>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Composer zone={zone} myName={myName} />
          </div>

          <Inbox
            zone={zone}
            hasPresence={presence.data !== null && presence.data !== undefined}
          />
        </div>
      </PageBody>
    </>
  )
}

export function MessagesPage() {
  return <RequireZone>{(zone) => <MessagesView zone={zone} />}</RequireZone>
}
